import React, { FC, useEffect, useState } from 'react';
import { DurationPickerModal, RoutineRecurringSelector } from '../../../../../Scheduler/common';
import { MealPreferenceItem, RoutineActivityRecurring } from '@demind-inc/core';
import { useGeneralSettings } from '../../../../../../hooks';
import dayjs from 'dayjs';
import { Stack, Typography } from '@mui/material';
import { translateMinToHHhMMmString } from '../../../../../../data-access';
import './MealSettingItem.scss';
import { ChevronRight } from '@mui/icons-material';
import { formatTimeFromHHMM } from '../../../../../../helpers';
import { TimePickerModal } from '../../../../../common';
import { useTranslation } from 'react-i18next';

interface MealSettingItemProps {
  mealPreferenceItem: MealPreferenceItem;
  onDurationChange: (duration: number) => void;
  onTimeChange: (start: string, end: string) => void;
  onRecurringChange: (recurring: RoutineActivityRecurring) => void;
  mealType: string;
}

const MealSettingItem: FC<MealSettingItemProps> = ({
  mealPreferenceItem,
  onDurationChange,
  onTimeChange,
  onRecurringChange,
  mealType,
}) => {
  const { t } = useTranslation();
  const [isDurationPickerModalOpen, setIsDurationPickerModalOpen] = useState(false);
  const [localMealPreferenceItem, setLocalMealPreferenceItem] = useState(mealPreferenceItem);
  const [isTimePickerModalOpen, setIsTimePickerModalOpen] = useState({
    earliest: false,
    latest: false,
  });
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();

  const mealName = t(`settings.aiAssistant.meals.${mealType}`);

  // Update local state if props change
  useEffect(() => {
    setLocalMealPreferenceItem(mealPreferenceItem);
  }, [mealPreferenceItem]);

  const handleStartTimeChange = (date: Date) => {
    const newStart = dayjs(date).format('HH:mm');
    setLocalMealPreferenceItem({ ...localMealPreferenceItem, earliestTime: newStart });
    onTimeChange(newStart, localMealPreferenceItem.latestTime);
  };

  const handleEndTimeChange = (date: Date) => {
    const newEnd = dayjs(date).format('HH:mm');
    setLocalMealPreferenceItem({ ...localMealPreferenceItem, latestTime: newEnd });
    onTimeChange(localMealPreferenceItem.earliestTime, newEnd);
  };

  const handleDurationChange = (h: number, m: number) => {
    const newDuration = h * 60 + m;
    setLocalMealPreferenceItem({ ...localMealPreferenceItem, durationMin: newDuration });
    onDurationChange(newDuration);
    setIsDurationPickerModalOpen(false);
  };

  const handleRecurringChange = (day: keyof RoutineActivityRecurring) => {
    const newRecurring: RoutineActivityRecurring = {
      ...localMealPreferenceItem.recurring,
      [day]: {
        ...localMealPreferenceItem.recurring[day],
        enabled: !localMealPreferenceItem.recurring[day].enabled,
      },
    };
    setLocalMealPreferenceItem({
      ...localMealPreferenceItem,
      recurring: newRecurring,
    });
    onRecurringChange(newRecurring);
  };

  const handleTimeChange = (time: Date) => {
    if (isTimePickerModalOpen.earliest) {
      setLocalMealPreferenceItem({ ...localMealPreferenceItem, earliestTime: time.toISOString() });
      handleStartTimeChange(dayjs(time).toDate());
    } else {
      setLocalMealPreferenceItem({ ...localMealPreferenceItem, latestTime: time.toISOString() });
      handleEndTimeChange(time);
    }
  };

  return (
    <Stack gap={0.5} className="meal-setting-item">
      <TimePickerModal
        isOpen={isTimePickerModalOpen.earliest || isTimePickerModalOpen.latest}
        onClose={() => setIsTimePickerModalOpen({ earliest: false, latest: false })}
        onTimeChange={handleTimeChange}
        title={t(
          isTimePickerModalOpen.earliest
            ? 'settings.aiAssistant.earliestMealTimeTitle'
            : 'settings.aiAssistant.latestMealTimeTitle',
          { meal: mealName }
        )}
        initialTime={
          isTimePickerModalOpen.earliest
            ? localMealPreferenceItem.earliestTime
            : localMealPreferenceItem.latestTime
        }
        timeFormat={timeFormat}
      />
      <DurationPickerModal
        isOpen={isDurationPickerModalOpen}
        onClose={() => setIsDurationPickerModalOpen(false)}
        onSave={handleDurationChange}
        title={t('settings.aiAssistant.setMealDuration', { meal: mealName })}
        initialDurationMin={mealPreferenceItem.durationMin}
      />
      <Stack
        onClick={() => setIsTimePickerModalOpen({ earliest: true, latest: false })}
        direction="row"
        justifyContent="space-between"
        className="meal-setting-item__item-container"
        alignItems="center"
      >
        <Typography className="meal-setting-item__item-title" variant="body2">
          {t('settings.aiAssistant.earliestStart')}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography className="meal-setting-item__item-value" variant="body2">
            {formatTimeFromHHMM(localMealPreferenceItem.earliestTime, timeFormat)}
          </Typography>
          <ChevronRight className="meal-setting-item__item-icon" />
        </Stack>
      </Stack>
      <Stack
        onClick={() => setIsTimePickerModalOpen({ earliest: false, latest: true })}
        direction="row"
        justifyContent="space-between"
        className="meal-setting-item__item-container"
        alignItems="center"
      >
        <Typography className="meal-setting-item__item-title" variant="body2">
          {t('settings.aiAssistant.latestEnd')}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography className="meal-setting-item__item-value" variant="body2">
            {formatTimeFromHHMM(localMealPreferenceItem.latestTime, timeFormat)}
          </Typography>
          <ChevronRight className="meal-setting-item__item-icon" />
        </Stack>
      </Stack>
      <Stack
        onClick={() => setIsDurationPickerModalOpen(true)}
        direction="row"
        justifyContent="space-between"
        className="meal-setting-item__item-container"
        alignItems="center"
      >
        <Typography className="meal-setting-item__item-title" variant="body2">
          {t('settings.aiAssistant.duration')}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography className="meal-setting-item__item-value" variant="body2">
            {translateMinToHHhMMmString(localMealPreferenceItem.durationMin)}
          </Typography>
          <ChevronRight className="meal-setting-item__item-icon" />
        </Stack>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className="meal-setting-item__item-container"
      >
        <Typography className="meal-setting-item__item-title" variant="body2">
          {t('settings.aiAssistant.repeatOn')}
        </Typography>

        <RoutineRecurringSelector
          recurring={localMealPreferenceItem.recurring}
          onDaySelect={handleRecurringChange}
          className="meal-setting-item__recurring-selector"
          hasSmallContainer
        />
      </Stack>
    </Stack>
  );
};

export default MealSettingItem;
